import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  chakra,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputProps,
} from '@chakra-ui/react';
import { TextStyles } from '@payler/ui-theme';
import { RequiredStyledProvider } from '@payler/ui-components';

const MaskedInput = chakra(Input, {
  baseStyle: {
    WebkitTextSecurity: 'disc',
    textSecurity: 'disc',
  },
});

export interface InputFieldProps
  extends Omit<InputProps, 'defaultValue' | 'type'> {
  name: string;
  label: string;
  helpText?: string;
}

export const CvvFieldComp: React.FC<InputFieldProps> = ({
  name,
  label,
  className,
  helpText,
  ...rest
}) => {
  const { control, getFieldState, formState } = useFormContext();
  const errorMessage = getFieldState(name, formState).error?.message;
  const isError = !!errorMessage && !!formState.touchedFields;
  return (
    <FormControl isInvalid={isError} {...rest}>
      <RequiredStyledProvider>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, onBlur, value, name, ref } }) => (
            <MaskedInput
              ref={ref}
              variant="floating"
              {...rest}
              name={name}
              value={value ?? ''}
              onBlur={onBlur}
              onChange={(e) => {
                const inputValue = e.target.value;
                const numericValue = inputValue.replace(/[^0-9]/g, '').slice(0, 3);
                onChange(numericValue || null);
              }}
              type="text"
              inputMode="numeric"
              maxLength={3}
            />
          )}
        />
        {label && <FormLabel variant="floating">{label}</FormLabel>}
        {helpText && (
          <FormHelperText
            color="primary.350"
            mt={1}
            textStyle={TextStyles.Caption12Regular}
            px={1.5}
          >
            {helpText}
          </FormHelperText>
        )}
        {isError && typeof errorMessage === 'string' && !rest.isDisabled && (
          <FormHelperText
            color="red.500"
            mt={1}
            textStyle={TextStyles.Caption12Regular}
            px={1.5}
          >
            {errorMessage}
          </FormHelperText>
        )}
      </RequiredStyledProvider>
    </FormControl>
  );
};
CvvFieldComp.displayName = 'CvvField';

/**
 * Поле для ввода CVV
 */
export const CvvField = chakra(CvvFieldComp);
CvvField.displayName = 'CvvField';
